<template id="app-template">
  <div id="app">
    <transition name="fade" mode="out-in">
      <component :is="currentComponent" v-if="currentComponent" :key="componentKey" />
    </transition>
    <!-- Global Inactivity Watcher -->
    <InactivityWatcher
      :inactivity-timeout="inactivityTimeout"
      :modal-duration="modalDuration"
      :enabled="inactivityWatcherEnabled"
    />
  </div>
</template>

<script>
import InactivityWatcher from '@/components/shared/InactivityWatcher.vue'
import PortalLayout from '@/layouts/PortalLayout.vue'
import TheMainView from '@/views/TheMainView.vue'
import portalFullscreenRoutes from '@/static-data/portalFullscreenRoutes.json'

export default {
  name: 'App',
  components: {
    InactivityWatcher,
    PortalLayout,
    TheMainView,
  },
  data() {
    return {}
  },
  computed: {
    componentKey() {
      return this.isPortalFullscreenRoute
        ? `${this.currentComponent}-${this.$route.name}`
        : this.currentComponent
    },
    currentComponent() {
      // If it's a fullscreen portal route that doesn't require a user session, return the component directly
      if (this.isPortalFullscreenRoute) {
        const matchedComponents = this.$router.getMatchedComponents()
        return matchedComponents.length > 0 ? matchedComponents[0] : null
      }

      // Otherwise use the layout logic
      return this.displayTheMainView
        ? 'TheMainView'
        : this.displayPortalLayout
        ? 'PortalLayout'
        : null
    },

    displayTheMainView() {
      return (this.layoutKey === 'default' && !this.isRoutingAfterLogin) || this.isLoginRoute
    },
    displayPortalLayout() {
      return this.layoutKey === 'portal' && !this.isRoutingAfterLogin && !this.isLoginRoute
    },

    isLoginRoute() {
      return this.$route.name === 'TheLogin'
    },
    isPortalFullscreenRoute() {
      const currentRoute = this.$route.name
      return portalFullscreenRoutes.some(route => route.name === currentRoute)
    },

    inactivityTimeout() {
      const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000 // 60 minutes in ms

      const durationInMinutes = this.sessionDurationInMinutes

      if (!durationInMinutes || typeof durationInMinutes !== 'number') {
        return DEFAULT_TIMEOUT_MS
      }

      const sessionDurationMs = durationInMinutes * 60 * 1000
      const modalDurationMs = this.modalDuration * 1000

      const timeoutBeforeWarningMs = Math.max(sessionDurationMs - modalDurationMs, 0)

      return timeoutBeforeWarningMs
    },
    // Enable/disable inactivity watcher globally
    inactivityWatcherEnabled() {
      return this.isSignedIn && !(this.isPortalUser && !this.$store.state.common.isPortalMSAApproved) && this.$store.state.common.isMSAApproved
    },

    isPortalUser() {
      return this.$store.state.common.isPortalUser
    },
    isRoutingAfterLogin() {
      return this.isPortalUser && this.isLoginRoute && this.$store.state.common.isPortalMSAApproved
    },

    isSignedIn() {
      return this.$store.state.common.isSignedIn
    },

    layoutKey() {
      return this.isPortalUser ? 'portal' : 'default'
    },

    modalDuration() {
      const MAX_WARNING_SECONDS = 120 // 2 minutes
      const MIN_WARNING_SECONDS = 10 // 10 seconds

      const durationInMinutes = this.sessionDurationInMinutes

      if (!durationInMinutes || typeof durationInMinutes !== 'number') {
        return MIN_WARNING_SECONDS * 1000
      }

      const durationInSeconds = durationInMinutes * 60
      const tenPercent = Math.floor(durationInSeconds * 0.1)

      const warningDuration = Math.min(tenPercent, MAX_WARNING_SECONDS)
      const boundedDuration = Math.max(warningDuration, MIN_WARNING_SECONDS)

      return boundedDuration // Seconds
    },
    sessionDurationInMinutes() {
      return this.$store.state.common.sessionDurationInMinutes
    },
  },
  async created() {
    await this.$store.dispatch('common/resetAxiosDefaultBaseURL')
  },
}
</script>

<style>
.modal-open {
  overflow: hidden;
}
</style>

<style lang="scss">
@import '@/assets/css/vasionStyles.scss';
</style>
